package com.heelion.farmafreelance.ui

import farmafreelance.composeapp.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Star
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import com.heelion.farmafreelance.models.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PreferitiScreen(
    viewModel: AuthViewModel,
    onBack: () -> Unit,
    onViewProfile: (String, UserRole) -> Unit
) {
    val preferiti = viewModel.currentPreferiti
    val themeColorHex = viewModel.selectedThemeColor.hex.removePrefix("0x").toLong(16)
    val themeColor = Color(themeColorHex)

    // Stato locale per memorizzare i profili caricati (per non rifare le chiamate ogni volta)
    val profiliCaricati = remember { mutableStateMapOf<String, Any>() }

    LaunchedEffect(preferiti) {
        preferiti.forEach { pref ->
            val targetId = pref.targetId
            if (!profiliCaricati.containsKey(targetId)) {
                if (pref.targetRuolo == "FARMACIA") {
                    viewModel.getFarmaciaById(targetId) { p ->
                        if (p != null) profiliCaricati[targetId] = p
                    }
                } else if (pref.targetRuolo == "INFERMIERE") {
                    viewModel.getInfermiereById(targetId) { p ->
                        if (p != null) profiliCaricati[targetId] = p
                    }
                } else {
                    viewModel.getFarmacistaById(targetId) { p ->
                        if (p != null) profiliCaricati[targetId] = p
                    }
                }
            }
        }
    }

    Scaffold(
        containerColor = Color.Transparent,
        topBar = {
            TopAppBar(
                colors = TopAppBarDefaults.topAppBarColors(containerColor = Color.Transparent),
                title = { Text("") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = stringResource(Res.string.back_nav))
                    }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .padding(horizontal = 16.dp)
        ) {
            // --- DASHBOARD STILE APP ---
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(
                    containerColor = if (themeColor == Color.White) Color.White.copy(alpha = 0.1f) else themeColor.copy(alpha = 0.08f)
                ),
                shape = RoundedCornerShape(16.dp),
                border = androidx.compose.foundation.BorderStroke(
                    width = 0.5.dp,
                    color = if (themeColor == Color.White) Color(0xFFC0C0C0) else themeColor.copy(alpha = 0.2f)
                ),
                elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
            ) {
                Column(modifier = Modifier.padding(16.dp)) {
                    Text(
                        text = stringResource(Res.string.my_favorites_title),
                        style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.Bold),
                        color = when {
                            themeColor == Color.White -> Color.Black
                            themeColor == Color(0xFF2196F3) -> Color(0xFF0D47A1)
                            else -> themeColor
                        }
                    )
                }
            }

            Spacer(modifier = Modifier.height(24.dp))

            if (preferiti.isEmpty()) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Icon(AppIcons.FavoriteBorder, contentDescription = null, modifier = Modifier.size(64.dp), tint = Color.LightGray)
                        Spacer(Modifier.height(16.dp))
                        Text(stringResource(Res.string.no_favorites_yet), color = Color.Gray)
                        Text(stringResource(Res.string.no_favorites_subtitle), style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                    }
                }
            } else {
                LazyColumn(
                    modifier = Modifier.fillMaxSize(),
                    verticalArrangement = Arrangement.spacedBy(12.dp),
                    contentPadding = PaddingValues(bottom = 16.dp)
                ) {
                    items(preferiti) { pref ->
                        val targetId = pref.targetId
                        val profilo = profiliCaricati[targetId]
                        
                        Card(
                            modifier = Modifier.fillMaxWidth().clickable {
                                val targetRole = when (pref.targetRuolo) {
                                    "FARMACIA" -> UserRole.FARMACIA
                                    "INFERMIERE" -> UserRole.INFERMIERE
                                    else -> UserRole.FARMACISTA
                                }
                                onViewProfile(targetId, targetRole)
                            },
                            shape = RoundedCornerShape(12.dp),
                            elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
                            colors = CardDefaults.cardColors(containerColor = Color.White)
                        ) {
                            Row(
                                modifier = Modifier.padding(16.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(16.dp)
                            ) {
                                Surface(
                                    shape = RoundedCornerShape(8.dp),
                                    color = themeColor.copy(alpha = 0.1f),
                                    modifier = Modifier.size(48.dp)
                                ) {
                                    Box(contentAlignment = Alignment.Center) {
                                        Icon(
                                            imageVector = if (pref.targetRuolo == "FARMACIA") AppIcons.Farmacia else AppIcons.Farmacista,
                                            contentDescription = null,
                                            tint = themeColor
                                        )
                                    }
                                }

                                Column(modifier = Modifier.weight(1f)) {
                                    val name = when (profilo) {
                                        is Farmacia -> profilo.nomeFarmacia
                                        is Professionista -> "${profilo.nome} ${profilo.cognome}"
                                        else -> stringResource(Res.string.loading)
                                    }
                                    val sub = when (profilo) {
                                        is Farmacia -> profilo.citta ?: profilo.indirizzo
                                        is Infermiere -> profilo.comuneOperativita ?: "Infermiere"
                                        is Farmacista -> profilo.comuneOperativita ?: stringResource(Res.string.farmacista)
                                        else -> ""
                                    }

                                    Text(name, fontWeight = FontWeight.Bold, fontSize = 16.sp)
                                    if (sub.isNotEmpty()) {
                                        Text(sub, style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                                    }
                                }

                                IconButton(onClick = { 
                                    viewModel.togglePreferito(
                                        targetId = pref.targetId, 
                                        targetRuolo = pref.targetRuolo
                                    )
                                }) {
                                    Icon(AppIcons.Favorite, contentDescription = stringResource(Res.string.remove_button), tint = Color.Red)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
